import {
  Button,
  Input,
  message,
  Modal,
  Select,
  Spin,
  Tag,
  Tooltip,
  Typography,
} from "antd";
import dayjs from "dayjs";
import React, {
  useCallback,
  useMemo,
  useState,
} from "react";
import {
  useCreateStaffAvailabilityList,
  useGetStaffAvailabilities,
  useUpdateStaffAvailabilityList,
} from "~/services/staff/staff-availability.queries";
import { useAuth } from "~/store";
import { StaffAvailability } from "~/utils/constants";
import { cn } from "~/utils";

const { Title, Text } = Typography;

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WEEKDAYS_SHORT = ["S", "M", "T", "W", "T", "F", "S"];

interface AvailabilityDetail {
  id?: string;
  date: string;
  availability: StaffAvailability;
  note?: string | null;
}

export const Availability: React.FC = () => {
  const { user } = useAuth();

  // Track week offset for navigation (0 = current week)
  const [weekOffset, setWeekOffset] = useState(0);

  // Track selected dates and pending changes
  const [selectedDates, setSelectedDates] = useState<Set<string>>(new Set());
  const [pendingAvailability, setPendingAvailability] = useState<StaffAvailability>(
    StaffAvailability.FULL_TIME
  );
  const [pendingNote, setPendingNote] = useState<string | undefined>(undefined);
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const staffId = useMemo(() => user?.staff?.id, [user?.staff?.id]);

  // Calculate the 2-week period starting from Sunday of current week
  const { days, startDate, endDate } = useMemo(() => {
    const baseWeekStart = dayjs().startOf("week").add(weekOffset, "week");
    const generatedDays: dayjs.Dayjs[] = [];

    for (let i = 0; i < 14; i++) {
      generatedDays.push(baseWeekStart.add(i, "day"));
    }

    return {
      days: generatedDays,
      startDate: generatedDays[0].startOf("day").toISOString(),
      endDate: generatedDays[13].endOf("day").toISOString(),
    };
  }, [weekOffset]);

  const {
    data: scheduleData,
    isLoading,
    refetch,
  } = useGetStaffAvailabilities({
    staffId,
    startDate,
    endDate,
    pageSize: 14,
  });

  const updateAvailabilityMutation = useUpdateStaffAvailabilityList();
  const createAvailabilityMutation = useCreateStaffAvailabilityList();

  // Create a map of dates to availability details
  const availabilityMap = useMemo(() => {
    const map: Record<string, AvailabilityDetail> = {};
    if (scheduleData?.items) {
      scheduleData.items.forEach((item: any) => {
        const dateStr = dayjs(item.date).format("YYYY-MM-DD");
        map[dateStr] = {
          id: item.id,
          date: dateStr,
          availability: item.availability,
          note: item.note,
        };
      });
    }
    return map;
  }, [scheduleData]);

  // Navigation handlers
  const goToPreviousWeeks = useCallback(() => {
    setWeekOffset((prev) => prev - 2);
    setSelectedDates(new Set());
  }, []);

  const goToNextWeeks = useCallback(() => {
    setWeekOffset((prev) => prev + 2);
    setSelectedDates(new Set());
  }, []);

  const goToToday = useCallback(() => {
    setWeekOffset(0);
    setSelectedDates(new Set());
  }, []);

  // Toggle date selection
  const toggleDate = useCallback((dateKey: string) => {
    setSelectedDates((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
  }, []);

  // Bulk selection handlers
  const selectAll = useCallback(() => {
    setSelectedDates(new Set(days.map((d) => d.format("YYYY-MM-DD"))));
  }, [days]);

  const selectNone = useCallback(() => {
    setSelectedDates(new Set());
  }, []);

  const selectWeekdays = useCallback(() => {
    const weekdays = days
      .filter((d) => d.day() >= 1 && d.day() <= 5)
      .map((d) => d.format("YYYY-MM-DD"));
    setSelectedDates(new Set(weekdays));
  }, [days]);

  // Get color class for a date cell
  const getDateColor = useCallback(
    (dateKey: string, isSelected: boolean) => {
      if (isSelected) {
        return "ring-2 ring-blue-500 ring-offset-1";
      }

      const detail = availabilityMap[dateKey];
      if (!detail) {
        return "bg-gray-100 border-gray-200";
      }

      if (detail.availability === StaffAvailability.UNAVAILABLE) {
        return "bg-red-100 border-red-200";
      }

      if (detail.availability === StaffAvailability.PART_TIME) {
        return "bg-yellow-100 border-yellow-200";
      }

      return "bg-green-100 border-green-200";
    },
    [availabilityMap]
  );

  // Get status label for tooltip
  const getStatusLabel = useCallback(
    (dateKey: string) => {
      const detail = availabilityMap[dateKey];
      if (!detail) {
        return "Not set";
      }

      let status = "";
      if (detail.availability === StaffAvailability.UNAVAILABLE) {
        status = "Unavailable";
      } else if (detail.availability === StaffAvailability.PART_TIME) {
        status = "Part Time";
      } else {
        status = "Available";
      }

      if (detail.note) {
        status += ` - ${detail.note}`;
      }

      return status;
    },
    [availabilityMap]
  );

  // Get readable label for availability
  const getAvailabilityLabel = (availability: StaffAvailability) => {
    switch (availability) {
      case StaffAvailability.FULL_TIME:
        return "Available (Full Day)";
      case StaffAvailability.PART_TIME:
        return "Part Time";
      case StaffAvailability.UNAVAILABLE:
        return "Unavailable";
      default:
        return "Unknown";
    }
  };

  // Save availability for selected dates
  const handleSave = async () => {
    if (!staffId) {
      message.error("User information is missing");
      return;
    }

    if (selectedDates.size === 0) {
      message.warning("Please select at least one date");
      return;
    }

    try {
      const datesToUpdate: { id: string; availability: StaffAvailability; note?: string }[] = [];
      const datesToCreate: { staffId: string; date: string; availability: StaffAvailability; note?: string }[] = [];

      selectedDates.forEach((dateKey) => {
        const existing = availabilityMap[dateKey];
        if (existing?.id) {
          datesToUpdate.push({
            id: existing.id,
            availability: pendingAvailability,
            note: pendingNote || undefined,
          });
        } else {
          datesToCreate.push({
            staffId,
            date: dateKey,
            availability: pendingAvailability,
            note: pendingNote || undefined,
          });
        }
      });

      const promises = [];

      if (datesToUpdate.length > 0) {
        promises.push(updateAvailabilityMutation.mutateAsync(datesToUpdate));
      }

      if (datesToCreate.length > 0) {
        promises.push(createAvailabilityMutation.mutateAsync(datesToCreate));
      }

      await Promise.all(promises);

      message.success(
        `Updated ${selectedDates.size} day${selectedDates.size > 1 ? "s" : ""}`
      );
      setSelectedDates(new Set());
      setPendingNote(undefined);
      refetch();
    } catch (error) {
      console.error("Failed to save availability:", error);
      message.error("Failed to save availability");
    }
  };

  const isSaving = updateAvailabilityMutation.isPending || createAvailabilityMutation.isPending;

  // Show confirmation modal before saving
  const handleSaveClick = () => {
    if (selectedDates.size === 0) {
      message.warning("Please select at least one date");
      return;
    }
    setShowConfirmModal(true);
  };

  // Confirm and save
  const handleConfirmSave = async () => {
    setShowConfirmModal(false);
    await handleSave();
  };

  // Check if today is in the current view
  const todayInView = days.some((d) => d.isSame(dayjs(), "day"));

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-48 md:h-64">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-3 md:space-y-4">
      <Title level={3} className="mb-0 text-lg md:text-xl">
        My Availability
      </Title>

      <div className="bg-white rounded-lg border p-3 md:p-4 space-y-3 md:space-y-4">
        {/* Legend - Compact on mobile */}
        <div className="flex flex-wrap gap-2 md:gap-3 text-[10px] md:text-xs border-b pb-2 md:pb-3">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 md:w-4 md:h-4 bg-green-100 border border-green-200 rounded" />
            <span>Available</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 md:w-4 md:h-4 bg-yellow-100 border border-yellow-200 rounded" />
            <span>Part Time</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 md:w-4 md:h-4 bg-red-100 border border-red-200 rounded" />
            <span>Unavailable</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 md:w-4 md:h-4 bg-gray-100 border border-gray-200 rounded" />
            <span>Not Set</span>
          </div>
        </div>

        {/* Navigation - Larger touch targets on mobile */}
        <div className="flex items-center justify-between gap-2">
          <Button
            onClick={goToPreviousWeeks}
            size="middle"
            className="min-w-[44px] h-[44px] md:min-w-0 md:h-auto"
          >
            &larr;
          </Button>
          <div className="flex flex-col items-center flex-1 min-w-0">
            <Text strong className="text-xs md:text-sm text-center truncate w-full">
              {days[0].format("MMM D")} - {days[13].format("MMM D, YYYY")}
            </Text>
            {!todayInView && (
              <Button size="small" type="link" onClick={goToToday} className="p-0 h-auto text-xs">
                Back to Today
              </Button>
            )}
          </div>
          <Button
            onClick={goToNextWeeks}
            size="middle"
            className="min-w-[44px] h-[44px] md:min-w-0 md:h-auto"
          >
            &rarr;
          </Button>
        </div>

        {/* Bulk Selection Buttons - Full width on mobile */}
        <div className="flex gap-1.5 md:gap-2 items-center">
          <Button
            size="middle"
            onClick={selectAll}
            className="flex-1 md:flex-none h-[40px] md:h-auto"
          >
            All
          </Button>
          <Button
            size="middle"
            onClick={selectNone}
            className="flex-1 md:flex-none h-[40px] md:h-auto"
          >
            Clear
          </Button>
          <Button
            size="middle"
            onClick={selectWeekdays}
            className="flex-1 md:flex-none h-[40px] md:h-auto"
          >
            M-F
          </Button>
          {selectedDates.size > 0 && (
            <Tag color="blue" className="ml-auto whitespace-nowrap">
              {selectedDates.size} sel.
            </Tag>
          )}
        </div>

        {/* 2-Week Calendar Grid */}
        <div className="space-y-1 md:space-y-2">
          {/* Weekday Headers - Single letter on mobile */}
          <div className="grid grid-cols-7 gap-1 md:gap-1.5">
            {WEEKDAYS.map((day, idx) => (
              <div
                key={day}
                className="text-center text-[10px] md:text-xs font-semibold text-gray-500 py-0.5 md:py-1"
              >
                <span className="hidden md:inline">{day}</span>
                <span className="md:hidden">{WEEKDAYS_SHORT[idx]}</span>
              </div>
            ))}
          </div>

          {/* Week 1 */}
          <div className="grid grid-cols-7 gap-1 md:gap-1.5">
            {days.slice(0, 7).map((day) => {
              const dateKey = day.format("YYYY-MM-DD");
              const isSelected = selectedDates.has(dateKey);
              const isToday = day.isSame(dayjs(), "day");
              const isWeekend = day.day() === 0 || day.day() === 6;
              const isFirstOfMonth = day.date() === 1;

              return (
                <Tooltip key={dateKey} title={getStatusLabel(dateKey)}>
                  <button
                    type="button"
                    onClick={() => toggleDate(dateKey)}
                    className={cn(
                      "p-1.5 md:p-3 rounded-md md:rounded-lg border text-center cursor-pointer transition-all active:scale-95 md:hover:scale-105 min-h-[52px] md:min-h-[70px] flex flex-col items-center justify-center",
                      getDateColor(dateKey, isSelected),
                      isToday && "ring-2 ring-blue-400",
                      isWeekend && "opacity-80"
                    )}
                  >
                    {/* Show month on first day for context */}
                    {isFirstOfMonth && (
                      <div className="text-[8px] md:text-[10px] text-gray-500 font-medium -mt-0.5">
                        {day.format("MMM")}
                      </div>
                    )}
                    <div className="text-sm md:text-lg font-semibold leading-tight">{day.format("D")}</div>
                    {isToday && (
                      <div className="text-[8px] md:text-[10px] text-blue-600 font-medium">Today</div>
                    )}
                  </button>
                </Tooltip>
              );
            })}
          </div>

          {/* Week 2 */}
          <div className="grid grid-cols-7 gap-1 md:gap-1.5">
            {days.slice(7, 14).map((day) => {
              const dateKey = day.format("YYYY-MM-DD");
              const isSelected = selectedDates.has(dateKey);
              const isToday = day.isSame(dayjs(), "day");
              const isWeekend = day.day() === 0 || day.day() === 6;
              const isFirstOfMonth = day.date() === 1;

              return (
                <Tooltip key={dateKey} title={getStatusLabel(dateKey)}>
                  <button
                    type="button"
                    onClick={() => toggleDate(dateKey)}
                    className={cn(
                      "p-1.5 md:p-3 rounded-md md:rounded-lg border text-center cursor-pointer transition-all active:scale-95 md:hover:scale-105 min-h-[52px] md:min-h-[70px] flex flex-col items-center justify-center",
                      getDateColor(dateKey, isSelected),
                      isToday && "ring-2 ring-blue-400",
                      isWeekend && "opacity-80"
                    )}
                  >
                    {/* Show month on first day for context */}
                    {isFirstOfMonth && (
                      <div className="text-[8px] md:text-[10px] text-gray-500 font-medium -mt-0.5">
                        {day.format("MMM")}
                      </div>
                    )}
                    <div className="text-sm md:text-lg font-semibold leading-tight">{day.format("D")}</div>
                    {isToday && (
                      <div className="text-[8px] md:text-[10px] text-blue-600 font-medium">Today</div>
                    )}
                  </button>
                </Tooltip>
              );
            })}
          </div>
        </div>

        {/* Set Availability Section - Sticky on mobile for easy access */}
        {selectedDates.size > 0 && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 md:p-4 space-y-2 md:space-y-3 sticky bottom-0 md:static shadow-lg md:shadow-none">
            <Text strong className="text-sm md:text-base">
              Set availability for {selectedDates.size} date
              {selectedDates.size > 1 ? "s" : ""}:
            </Text>

            <Select
              value={pendingAvailability}
              onChange={setPendingAvailability}
              className="w-full"
              size="large"
              options={[
                { value: StaffAvailability.FULL_TIME, label: "Available (Full Day)" },
                { value: StaffAvailability.PART_TIME, label: "Part Time" },
                { value: StaffAvailability.UNAVAILABLE, label: "Unavailable" },
              ]}
            />

            <Input.TextArea
              value={pendingNote}
              onChange={(e) => setPendingNote(e.target.value || undefined)}
              placeholder="Add a note (optional)"
              rows={2}
              className="w-full"
              size="large"
            />

            <Button
              type="primary"
              onClick={handleSaveClick}
              loading={isSaving}
              block
              size="large"
              className="h-[48px] text-base font-medium"
            >
              Save
            </Button>
          </div>
        )}

        {/* Empty state */}
        {selectedDates.size === 0 && (
          <div className="text-center text-gray-400 py-3 md:py-2 text-sm">
            Tap dates to select, then set your availability
          </div>
        )}
      </div>

      {/* Confirmation Modal */}
      <Modal
        title="Confirm Availability Update"
        open={showConfirmModal}
        onOk={handleConfirmSave}
        onCancel={() => setShowConfirmModal(false)}
        okText="Confirm"
        cancelText="Cancel"
        confirmLoading={isSaving}
      >
        <div className="space-y-3 py-2">
          <p>You are about to update your availability for:</p>

          {/* List of selected dates */}
          <div className="bg-gray-50 rounded-lg p-3 max-h-[150px] overflow-y-auto">
            <div className="flex flex-wrap gap-1.5">
              {Array.from(selectedDates)
                .sort()
                .map((dateKey) => (
                  <span
                    key={dateKey}
                    className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-md font-medium"
                  >
                    {dayjs(dateKey).format("ddd, MMM D")}
                  </span>
                ))}
            </div>
          </div>

          {/* Status and note */}
          <div className="bg-gray-50 rounded-lg p-3 space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Status:</span>
              <span className="font-medium">{getAvailabilityLabel(pendingAvailability)}</span>
            </div>
            {pendingNote && (
              <div className="flex flex-col gap-1">
                <span className="text-gray-600">Note:</span>
                <span className="font-medium text-sm bg-white p-2 rounded border">{pendingNote}</span>
              </div>
            )}
          </div>

          <p className="text-sm text-gray-500">
            This will be visible to admins immediately.
          </p>
        </div>
      </Modal>
    </div>
  );
};
